import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useLang, tr } from "../contexts/LangContext";
import { withLang } from "./SeoHead";
import { X, CalendarDays, MapPin } from "lucide-react";

/* Pop-up affichant le prochain événement.
   Dismissal stored per event-id in localStorage so each event re-shows on first visit. */
export default function HomeEventPopup() {
  const { lang } = useLang();
  const [event, setEvent] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/events/upcoming-featured");
        if (cancelled || !data || !data.id) return;
        const key = `rycm-event-popup-dismissed:${data.id}`;
        if (typeof window !== "undefined" && window.localStorage.getItem(key)) return;
        setEvent(data);
        // Slight delay so the popup feels intentional
        setTimeout(() => !cancelled && setOpen(true), 900);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = (remember = true) => {
    if (event && remember) {
      try { window.localStorage.setItem(`rycm-event-popup-dismissed:${event.id}`, "1"); } catch { /* ignore */ }
    }
    setOpen(false);
  };

  if (!open || !event) return null;
  const title = event[`title_${lang}`] || event.title_fr || tr("Événement à venir");
  const description = event[`description_${lang}`] || event.description_fr || "";
  const dateStr = (() => {
    try {
      return new Date(event.date).toLocaleDateString(lang === "en" ? "en-GB" : lang, {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      });
    } catch { return event.date; }
  })();

  return (
    <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center p-3 md:p-4" data-testid="event-popup">
      {/* backdrop */}
      <div className="absolute inset-0 bg-navy/70 backdrop-blur-sm" onClick={() => dismiss(true)} />

      {/* card */}
      <div className="relative w-full max-w-xl bg-white border border-slate-200 shadow-2xl animate-[fadeIn_0.4s_ease-out]">
        {/* Top yellow ribbon */}
        <div className="h-1 bg-club-yellow"></div>

        <button
          onClick={() => dismiss(true)}
          className="absolute top-3 right-3 text-slate-500 hover:text-club-red transition-colors p-1"
          aria-label="Fermer"
          data-testid="event-popup-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 md:p-8">
          <div className="label-eyebrow text-club-red mb-2">{tr("Prochain événement RYCM")}</div>
          <h2 className="font-serif text-2xl md:text-3xl text-navy leading-tight" data-testid="event-popup-title">
            {title}
          </h2>
          <div className="divider-gold mt-3 mb-4 max-w-[80px]" />

          <div className="space-y-2 text-sm text-slate-700">
            <div className="flex items-center gap-2" data-testid="event-popup-date">
              <CalendarDays className="w-4 h-4 text-club-red shrink-0" />
              <span className="capitalize">{dateStr}</span>
            </div>
            {event.location && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-club-red shrink-0" />
                <span>{event.location}</span>
              </div>
            )}
          </div>

          {description && (
            <p className="text-sm text-slate-600 mt-4 leading-relaxed line-clamp-4">{description}</p>
          )}

          <div className="flex flex-col sm:flex-row gap-2 mt-6">
            <Link
              to={withLang(lang, "/agenda")}
              onClick={() => dismiss(false)}
              className="flex-1 bg-club-red text-white py-3 text-center text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark transition-colors"
              data-testid="event-popup-cta"
            >
              Voir l'agenda complet
            </Link>
            <button
              onClick={() => dismiss(true)}
              className="flex-1 border border-navy text-navy py-3 text-xs uppercase tracking-[0.2em] hover:bg-navy hover:text-white transition-colors"
              data-testid="event-popup-dismiss"
            >
              Plus tard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
